import { useState, useRef, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Popover, PopoverTrigger, PopoverContent, Textarea } from '@scio3/ui';
import { toast } from 'sonner';
import { analyticsOptibotFeedback } from '@/lib/analytics-service';

interface FeedbackButtonsProps {
  messageId: string;
  onFeedback: (messageId: string, score: number, comment?: string) => void;
}

const AUTO_DISMISS_MS = 10_000;

/**
 * Thumbs-up / thumbs-down feedback buttons for assistant messages.
 * Always visible, locks after first click.
 *
 * Thumbs-up: immediate send (score 1).
 * Thumbs-down: opens popover for optional comment, then sends (score 0).
 */
export function FeedbackButtons({ messageId, onFeedback }: FeedbackButtonsProps) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState<number | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [comment, setComment] = useState('');
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  // Auto-dismiss popover after 10s of no interaction
  useEffect(() => {
    if (!popoverOpen) return;
    clearDismissTimer();
    dismissTimerRef.current = setTimeout(() => {
      // Send without comment on auto-dismiss
      setPopoverOpen(false);
      setSubmitted(0);
      onFeedback(messageId, 0);
      analyticsOptibotFeedback({ rating: 'negative' });
      toast.success(t('optibot.feedbackThanks'));
    }, AUTO_DISMISS_MS);
    return clearDismissTimer;
  }, [popoverOpen, clearDismissTimer, messageId, onFeedback, t]);

  const handleThumbsUp = () => {
    if (submitted !== null) return;
    setSubmitted(1);
    onFeedback(messageId, 1);
    analyticsOptibotFeedback({ rating: 'positive' });
    toast.success(t('optibot.feedbackThanks'));
  };

  const handleThumbsDownClick = () => {
    if (submitted !== null) return;
    setPopoverOpen(true);
  };

  const handleSendComment = () => {
    clearDismissTimer();
    setPopoverOpen(false);
    setSubmitted(0);
    onFeedback(messageId, 0, comment.trim() || undefined);
    analyticsOptibotFeedback({ rating: 'negative' });
    toast.success(t('optibot.feedbackThanks'));
  };

  // Reset auto-dismiss timer on user interaction with the textarea
  const handleCommentChange = (value: string) => {
    setComment(value);
    // Reset the auto-dismiss timer when user interacts
    if (popoverOpen) {
      clearDismissTimer();
      dismissTimerRef.current = setTimeout(() => {
        setPopoverOpen(false);
        setSubmitted(0);
        onFeedback(messageId, 0, value.trim() || undefined);
        analyticsOptibotFeedback({ rating: 'negative' });
        toast.success(t('optibot.feedbackThanks'));
      }, AUTO_DISMISS_MS);
    }
  };

  return (
    <div className="flex items-center gap-1" data-testid="feedback-buttons">
      <Button
        variant="ghost"
        size="icon-xs"
        className={cn(submitted === 1 && 'text-primary')}
        disabled={submitted !== null}
        onClick={handleThumbsUp}
        aria-label={t('optibot.feedbackThumbsUp')}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className={cn(submitted === 0 && 'text-destructive')}
            disabled={submitted !== null}
            onClick={handleThumbsDownClick}
            aria-label={t('optibot.feedbackThumbsDown')}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-3"
          side="top"
          align="start"
          onInteractOutside={() => {
            // Send without comment if clicking outside
            handleSendComment();
          }}
        >
          <div className="flex flex-col gap-2">
            <Textarea
              placeholder={t('optibot.feedbackPrompt')}
              value={comment}
              onChange={(e) => handleCommentChange(e.target.value)}
              rows={3}
              className="resize-none text-sm"
              data-testid="feedback-comment"
            />
            <Button size="sm" onClick={handleSendComment} className="self-end">
              {t('optibot.feedbackSend')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
